/**
 * Imperative pub/sub channel for live mic levels.
 *
 * The analyser rAF loop produces 5-band level frames at display rate (~60Hz).
 * Routing those through React state at the session root re-rendered the whole
 * orchestrator tree once per frame for the full duration of every recording
 * (2026-07-18 primetime audit CRITICAL). This store inverts the flow: the
 * capture hook WRITES frames here; only the waveform leaf SUBSCRIBES (via
 * useSyncExternalStore in LiveWaveform, reading getSnapshot()); guards that
 * need a point-in-time reading (silence detection) call getLatest() — never
 * getSnapshot(), whose value is display-deduped and can lag the newest raw
 * frame around the silence threshold (Codex r2 P2 / r3 P3).
 *
 * Frames are quantized to 0.01 before publishing, and identical consecutive
 * frames do not notify — sustained silence or a settled level costs zero
 * renders.
 */

export const ZERO_LEVELS: readonly number[] = Object.freeze([0, 0, 0, 0, 0]);

export interface AudioLevelStore {
  /**
   * The last NOTIFIED 5-band frame — reference-stable unless a notification
   * fired (the useSyncExternalStore contract; LiveWaveform reads this).
   */
  getSnapshot(): readonly number[];
  /**
   * The newest RAW frame, updated on EVERY set() even when display-deduped.
   * Imperative guards (silence detection) must read this one: a deduped
   * 0.151-after-0.149 frame would otherwise leave the guard on the stale
   * 0.149 (Codex r2 P2). Never hand this to useSyncExternalStore — it can
   * change identity without a notification.
   */
  getLatest(): readonly number[];
  subscribe(listener: () => void): () => void;
}

export interface WritableAudioLevelStore extends AudioLevelStore {
  set(bands: readonly number[]): void;
}

const QUANT = 100; // 0.01 resolution — below visual perceptibility for the waveform

/** Dedupe key comparison only — snapshots stay RAW so guard reads (silence
 *  detection's strict `< threshold`) see unrounded analyser values. Rounding
 *  the stored frame would turn a raw 0.149 into 0.15 and flip the legacy
 *  strict-less-than silence classification (Codex r1 P2). */
function quantizedEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.round(a[i] * QUANT) !== Math.round(b[i] * QUANT)) return false;
  }
  return true;
}

export function createAudioLevelStore(): WritableAudioLevelStore {
  let notified: readonly number[] = ZERO_LEVELS;
  let latest: readonly number[] = ZERO_LEVELS;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => notified,
    getLatest: () => latest,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set(bands) {
      latest = [...bands];
      if (quantizedEqual(notified, latest)) return;
      notified = latest;
      listeners.forEach((listener) => listener());
    },
  };
}

/**
 * Fixed-frame store for fixtures, test pages, and "not this prompt" scoping
 * fallbacks (FieldBlock hides another prompt's live levels by substituting a
 * static store). Never notifies.
 */
export function staticLevelStore(bands: readonly number[]): AudioLevelStore {
  const frame = Object.freeze([...bands]);
  return {
    getSnapshot: () => frame,
    getLatest: () => frame,
    subscribe: () => () => {},
  };
}

/** Shared silent store for "no capture in flight" fallbacks. */
export const SILENT_LEVEL_STORE: AudioLevelStore = staticLevelStore(ZERO_LEVELS);
